import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ClipboardCheck, Send } from "lucide-react";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const chipTone = (s) => ({
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  submitted: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
}[s] || "bg-slate-100 text-slate-700 border-slate-300");

/**
 * Working Draft queue + review panel for BudgetV2.
 *
 * Accounts / Admin: see their own drafts (draft + submitted + past decisions).
 * Approver:        see submitted + past decisions ONLY. Sanctioned figures are
 *                  server-stripped for their role.
 */
export default function WorkingDraftsPanel({ projectId, userRole, openDraftId, onApproved }) {
  const [drafts, setDrafts] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    if (!projectId) return;
    api.get(`/v2/budgets/working/drafts?project_id=${projectId}`).then(r => setDrafts(r.data)).catch(() => setDrafts([]));
  };
  useEffect(load, [projectId]);

  // Auto-open the review dialog when we arrived via ?approve_draft=<id>
  // from the approvals inbox. Only fire once per id.
  useEffect(() => {
    if (openDraftId && openDraftId !== openId) setOpenId(openDraftId);
  }, [openDraftId]);

  const isApprover = userRole === "Approver" || userRole === "Authorizer" || userRole === "Admin";
  const isAccounts = userRole === "Accounts" || userRole === "Admin";
  if (!isApprover && !isAccounts) return null;
  if (!projectId) return null;
  if (drafts.length === 0) {
    if (userRole === "Approver" || userRole === "Authorizer") return null;
    return (
      <Card className="border-dashed" data-testid="wd-empty-card">
        <CardContent className="p-4 text-xs text-slate-500">No working drafts on this project yet.</CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="working-drafts-panel">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" /> Working Drafts
        </CardTitle>
        <span className="text-[11px] text-slate-500">{drafts.length} total</span>
      </CardHeader>
      <CardContent className="pt-0">
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b">
              <th className="text-left px-2 py-1">Created</th>
              <th className="text-left px-2 py-1">By</th>
              <th className="text-right px-2 py-1">Rows</th>
              <th className="text-right px-2 py-1">Working total</th>
              <th className="text-left px-2 py-1">Status</th>
              <th className="text-left px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {drafts.map(d => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wd-row-${d.id}`}>
                <td className="px-2 py-1 font-mono">{(d.created_at || "").slice(0, 10)}</td>
                <td className="px-2 py-1">{d.created_by_name || "—"}</td>
                <td className="px-2 py-1 text-right font-mono">{d.row_count}</td>
                <td className="px-2 py-1 text-right font-mono">{rupee(d.totals?.project_total_file)}</td>
                <td className="px-2 py-1">
                  <span className={`px-1.5 py-0.5 border rounded text-[10px] uppercase font-medium ${chipTone(d.status)}`}>{d.status}</span>
                </td>
                <td className="px-2 py-1">
                  <Button size="sm" variant="outline" onClick={() => setOpenId(d.id)} data-testid={`wd-open-${d.id}`}>
                    Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      {openId && (
        <WorkingDraftReviewDialog
          draftId={openId}
          userRole={userRole}
          onClose={() => setOpenId(null)}
          onChanged={() => { load(); onApproved?.(); }}
        />
      )}
    </Card>
  );
}

function WorkingDraftReviewDialog({ draftId, userRole, onClose, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [checks, setChecks] = useState({ yes_no_within_sanctioned: false, yes_no_above_committed: false, yes_no_periods_valid: false });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/v2/budgets/working/drafts/${draftId}`).then(r => {
      setDraft(r.data);
      // Pre-tick server-computed Yes answers as a starting point.
      const s = r.data.yes_no_summary || {};
      setChecks({
        yes_no_within_sanctioned: !!s.within_sanctioned_total,
        yes_no_above_committed: !!s.no_line_below_committed,
        yes_no_periods_valid: !!s.all_periods_labelled,
      });
    }).catch(e => toast.error(e.response?.data?.detail || "Load failed"));
  }, [draftId]);

  const isAccounts = userRole === "Accounts" || userRole === "Admin";
  const isApprover = userRole === "Approver" || userRole === "Authorizer" || userRole === "Admin";
  const canSubmit = draft?.status === "draft" && isAccounts;
  const canDecide = draft?.status === "submitted" && isApprover;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/v2/budgets/working/drafts/${draftId}/submit`);
      toast.success("Submitted for approval");
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/v2/budgets/working/drafts/${draftId}/approve`, checks);
      toast.success("Approved — working budget updated");
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    setBusy(false);
  };
  const reject = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/v2/budgets/working/drafts/${draftId}/reject`, { reason });
      toast.success("Rejected");
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="wd-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            Working Draft · {draft?.project_code} · {draft?.status?.toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        {!draft ? <div className="p-6 text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Created {draft.created_at?.slice(0, 10)} by <b>{draft.created_by_name}</b> ·
              File: <span className="font-mono">{draft.file_name}</span>
              {draft.status === "rejected" && (
                <div className="mt-1 text-rose-700">Rejected by {draft.rejected_by_name}: {draft.rejection_reason}</div>
              )}
            </div>

            {/* Compliance checks — visible to both, editable only by Approver on submitted drafts. */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="wd-checks">
              <div className="text-xs font-medium text-slate-600 mb-1">Compliance checks</div>
              {[
                { key: "yes_no_within_sanctioned", label: "Working total is within the project ceiling", auto: draft.yes_no_summary?.within_sanctioned_total },
                { key: "yes_no_above_committed", label: "No line is below its already-committed (spent + pending) amount", auto: draft.yes_no_summary?.no_line_below_committed },
                { key: "yes_no_periods_valid", label: "Every row carries a period label", auto: draft.yes_no_summary?.all_periods_labelled },
              ].map(c => (
                <label key={c.key} className="flex items-center gap-2 text-xs">
                  <input type="checkbox"
                    checked={checks[c.key]}
                    disabled={!canDecide}
                    onChange={(e) => setChecks({ ...checks, [c.key]: e.target.checked })}
                    data-testid={`wd-check-${c.key}`} />
                  <span>{c.label}</span>
                  <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded border ${c.auto ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-800"}`}>
                    Auto: {c.auto ? "Yes" : "No"}
                  </span>
                </label>
              ))}
            </div>

            {/* Row table — sanctioned amounts hidden for Approvers server-side. */}
            <div className="text-xs font-medium text-slate-600">Working figures ({draft.rows?.length} rows) · total {rupee(draft.totals?.project_total_file)}</div>
            <div className="max-h-[35vh] overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Period</th>
                    <th className="text-left px-2 py-1">Sub-Type</th>
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-right px-2 py-1">Working</th>
                    <th className="text-left px-2 py-1">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(draft.rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.period_label}</td>
                      <td className="px-2 py-1 font-mono">{r.sub_type_code}</td>
                      <td className="px-2 py-1">{r.sub_type_name}</td>
                      <td className="px-2 py-1 text-right font-mono">{rupee(r.working_amount)}</td>
                      <td className="px-2 py-1 text-slate-500 truncate max-w-[240px]">{r.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {canDecide && (
              <div>
                <label className="text-xs text-slate-600">Rejection reason (only needed if rejecting)</label>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="wd-reject-reason" />
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          {canSubmit && (
            <Button onClick={submit} disabled={busy} data-testid="wd-submit-btn">
              <Send className="w-4 h-4 mr-1" /> Submit for approval
            </Button>
          )}
          {canDecide && (
            <>
              <Button variant="outline" onClick={reject} disabled={busy || !reason.trim()} data-testid="wd-reject-btn">
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
              <Button onClick={approve} disabled={busy || !Object.values(checks).every(Boolean)} data-testid="wd-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
